/**
 * The running order on the playout screen: rundowns → segments → parts, the
 * On Air and Next parts in their tally colours, each segment's budget
 * against its pace, a queued segment marked, and the segment commands (set
 * next segment now, queue it after the current one).
 *
 * **Virtualised** (TanStack Virtual, as the editor's tree): the running order
 * is one flat list of rows — a rundown's heading, a segment's header, a part —
 * and only the rows in view (and a margin) are in the DOM, each measured as
 * it renders, so a 1,000-part show scrolls and re-renders every tick as a
 * short one does. The component is its own scroll container.
 *
 * The part cursor (`selectedPartId`) is the keyboard's; the view moves it.
 * The cursor's row — or, without a cursor, the part going on air — is
 * scrolled into view when it changes (nothing moves when it already is).
 */
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useMemo, useRef } from 'react';
import { formatLength, formatOverUnder } from '../../lib/playout/format.js';
import type { PartView, PlayoutView, RundownView, SegmentView } from '../../lib/playout/viewModel.js';
import { cn } from '../../lib/utils.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { PlayoutPartRow } from './PlayoutPartRow.js';

export type PlayoutRundownProps = {
  readonly view: PlayoutView;
  readonly selectedPartId: string | null;
  readonly onAirClock: { readonly elapsedMs: number; readonly remainingMs: number | null } | null;
  /** Next may be set (`playout.take`, an active playlist). */
  readonly canCommand: boolean;
  readonly onSelect: (partId: string) => void;
  readonly onSetNext: (partId: string) => void;
  readonly onSetNextSegment: (segmentId: string) => void;
  readonly onQueueSegment: (segmentId: string) => void;
  /** The viewport height the virtualiser assumes before it can measure (server rendering, first paint). */
  readonly initialHeight?: number;
};

/** One row of the flattened running order. */
type Row =
  | { readonly kind: 'rundown'; readonly key: string; readonly rundown: RundownView }
  | { readonly kind: 'segment'; readonly key: string; readonly segment: SegmentView }
  | { readonly kind: 'part'; readonly key: string; readonly part: PartView; readonly segment: SegmentView };

/** Estimated heights before a row is measured (px): a heading, a segment header, a part with its piece strip. */
const ESTIMATE: Readonly<Record<Row['kind'], number>> = { rundown: 28, segment: 40, part: 76 };

/** The running order as rows, and where each part is among them. */
function flatten(view: PlayoutView): { readonly rows: readonly Row[]; readonly partIndex: ReadonlyMap<string, number> } {
  const rows: Row[] = [];
  const partIndex = new Map<string, number>();
  for (const rundown of view.rundowns) {
    if (view.rundowns.length > 1) rows.push({ kind: 'rundown', key: `r:${rundown.rundownId}`, rundown });
    for (const segment of rundown.segments) {
      rows.push({ kind: 'segment', key: `s:${segment.segmentId}`, segment });
      for (const part of segment.parts) {
        if (!partIndex.has(part.partId)) partIndex.set(part.partId, rows.length);
        rows.push({ kind: 'part', key: `p:${part.partId}:${part.instanceId ?? ''}`, part, segment });
      }
    }
  }
  return { rows, partIndex };
}

function SegmentHeader({ segment, canCommand, onSetNextSegment, onQueueSegment }: { readonly segment: SegmentView } & Pick<PlayoutRundownProps, 'canCommand' | 'onSetNextSegment' | 'onQueueSegment'>) {
  const overUnder = formatOverUnder(segment.overUnderMs);
  return (
    <div className="flex flex-wrap items-center gap-2 border-b bg-muted px-3 py-1.5" data-testid="segment-header" data-segment-id={segment.segmentId}>
      <span className="font-semibold" data-testid="segment-name">
        {segment.name}
      </span>
      {segment.hidden ? <Badge variant="outline">Hidden</Badge> : null}
      {segment.queued ? <Badge variant="standby">Queued next</Badge> : null}
      <span className="ml-auto flex items-center gap-3 font-mono text-xs text-muted-foreground tabular-nums" data-testid="segment-timing">
        <span title="Planned length of its parts">Planned {formatLength(segment.expectedMs)}</span>
        <span title="Played in this activation">Played {formatLength(segment.playedMs)}</span>
        {segment.budgetMs === null ? null : (
          <>
            <span title="The segment's budget">Budget {formatLength(segment.budgetMs)}</span>
            <span
              className={cn('rounded px-1', overUnder.tone === 'over' && 'bg-onair text-onair-foreground', overUnder.tone === 'under' && 'bg-next text-next-foreground')}
              data-testid="segment-over-under"
              data-tone={overUnder.tone}
            >
              {overUnder.text}
            </span>
          </>
        )}
      </span>
      {segment.offRunningOrder ? null : (
        <span className="flex gap-1">
          <Button size="sm" variant="ghost" disabled={!canCommand} onClick={() => onSetNextSegment(segment.segmentId)} data-testid="set-next-segment">
            Next segment
          </Button>
          <Button size="sm" variant="ghost" disabled={!canCommand || segment.queued} onClick={() => onQueueSegment(segment.segmentId)} data-testid="queue-segment">
            Queue
          </Button>
        </span>
      )}
    </div>
  );
}

export function PlayoutRundown(props: PlayoutRundownProps) {
  const { view, selectedPartId, onAirClock, canCommand, onSelect, onSetNext, onSetNextSegment, onQueueSegment, initialHeight } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const { rows, partIndex } = useMemo(() => flatten(view), [view]);

  // The rule warns that React Compiler cannot memoise around TanStack Virtual's
  // mutable instance. This build does not use React Compiler, and this is the
  // library's documented usage; nothing memoised receives the instance.
  // oxlint-disable-next-line react/incompatible-library
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => ESTIMATE[rows[index]?.kind ?? 'part'],
    getItemKey: (index) => rows[index]?.key ?? String(index),
    overscan: 8,
    initialRect: { width: 1000, height: initialHeight ?? 2000 },
  });

  // Keep the cursor — or, without one, the part going on air — in view.
  const target = selectedPartId ?? view.onAirPartId;
  const targetIndex = target === null ? -1 : (partIndex.get(target) ?? -1);
  useEffect(() => {
    if (targetIndex >= 0) virtualizer.scrollToIndex(targetIndex, { align: 'auto' });
  }, [targetIndex, virtualizer]);

  if (view.partOrder.length === 0) {
    return (
      <p className="p-8 text-center text-sm text-muted-foreground" data-testid="playout-rundown-empty">
        The running order is empty: add segments and parts in the rundown editor.
      </p>
    );
  }
  return (
    <div ref={scrollRef} className="h-full overflow-y-auto" data-testid="playout-rundown" data-rows={rows.length}>
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((item) => {
          const row = rows[item.index];
          if (row === undefined) return null;
          return (
            <div key={item.key} ref={virtualizer.measureElement} data-index={item.index} className="absolute left-0 top-0 w-full" style={{ transform: `translateY(${String(item.start)}px)` }}>
              {row.kind === 'rundown' ? <h2 className="bg-secondary px-3 py-1 text-sm font-semibold text-secondary-foreground">{row.rundown.name}</h2> : null}
              {row.kind === 'segment' ? (
                <div className={cn(row.segment.hidden && 'opacity-60')}>
                  <SegmentHeader segment={row.segment} canCommand={canCommand} onSetNextSegment={onSetNextSegment} onQueueSegment={onQueueSegment} />
                </div>
              ) : null}
              {row.kind === 'part' ? (
                <div className={cn('border-b', row.segment.hidden && 'opacity-60')} data-segment-id={row.segment.segmentId}>
                  <PlayoutPartRow
                    part={row.part}
                    selected={row.part.partId === selectedPartId}
                    onAirClock={row.part.status === 'onair' ? onAirClock : null}
                    canSetNext={canCommand}
                    onSelect={() => onSelect(row.part.partId)}
                    onSetNext={() => onSetNext(row.part.partId)}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
